/**
 * VideoSplitter - Client-Side Video Splitting Engine (TypeScript)
 */

export interface ProgressInfo {
  percent: number;
  text: string;
  currentChunk?: number;
  totalChunks?: number;
}

export interface SplitOptions {
  videoFile: File | null;
  videoUrl: string | null;
  totalDuration: number;
  clipDurationSec?: number;
  partCount?: number;
  splitMode?: 'duration' | 'parts';
  useTimeRange?: boolean;
  startTime?: number;
  endTime?: number;
  onProgress?: (info: ProgressInfo) => void;
}

export interface SplitResultClip {
  id: number;
  name: string;
  url: string;
  blob: Blob;
  duration: number;
  start: number;
  end: number;
  thumbnailUrl?: string;
}

export class SplitterEngine {

  public static async splitVideo(options: SplitOptions): Promise<SplitResultClip[]> {
    const {
      videoFile,
      videoUrl,
      totalDuration,
      clipDurationSec = 60,
      partCount = 2,
      splitMode = 'duration',
      useTimeRange = false,
      startTime = 0,
      endTime = totalDuration,
      onProgress = () => {}
    } = options;

    const rangeStart = useTimeRange ? startTime : 0;
    const rangeEnd = useTimeRange && endTime > startTime ? endTime : totalDuration;
    const effectiveDuration = rangeEnd - rangeStart;

    if (effectiveDuration <= 0) {
      throw new Error("Invalid video time range specified.");
    }

    const segments: Array<{ index: number; start: number; end: number; duration: number }> = [];

    if (splitMode === 'parts') {
      const segDuration = effectiveDuration / Math.max(1, partCount);
      for (let i = 0; i < partCount; i++) {
        const segStart = rangeStart + (i * segDuration);
        const segEnd = (i === partCount - 1) ? rangeEnd : segStart + segDuration;
        segments.push({
          index: i + 1,
          start: segStart,
          end: segEnd,
          duration: segEnd - segStart
        });
      }
    } else {
      const clipLen = Math.max(1, clipDurationSec);
      let curr = rangeStart;
      let idx = 1;
      while (curr < rangeEnd) {
        const segEnd = Math.min(rangeEnd, curr + clipLen);
        segments.push({
          index: idx++,
          start: curr,
          end: segEnd,
          duration: segEnd - curr
        });
        curr = segEnd;
      }
    }

    onProgress({ percent: 5, text: 'Initializing video stream...' });

    const baseName = videoFile ? videoFile.name.replace(/\.[^/.]+$/, "") : "video_clip";
    const results: SplitResultClip[] = [];

    const video = document.createElement('video');
    video.style.display = 'none';
    video.muted = true;
    video.crossOrigin = 'anonymous';
    document.body.appendChild(video);

    const srcUrl = videoUrl || (videoFile ? URL.createObjectURL(videoFile) : '');
    video.src = srcUrl;

    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error("Failed to load video file for splitting."));
    });

    const totalSegs = segments.length;
    const baseProgress = 5;
    const allocProgress = 90;

    for (let i = 0; i < totalSegs; i++) {
      const seg = segments[i];
      const segBasePercent = baseProgress + (i / totalSegs) * allocProgress;
      const segWeight = (1 / totalSegs) * allocProgress;

      let clipBlob: Blob | null = null;
      let clipUrl = '';
      let thumbUrl = '';

      onProgress({
        percent: Math.round(segBasePercent),
        text: `Extracting thumbnail for clip ${i + 1} of ${totalSegs}...`,
        currentChunk: i + 1,
        totalChunks: totalSegs
      });

      try {
        thumbUrl = await this.captureThumbnail(video, seg.start + Math.min(0.5, seg.duration / 2));
      } catch (e) {
        console.warn("Thumbnail generation warning:", e);
      }

      try {
        clipBlob = await this.recordVideoSegment(video, seg.start, seg.end, (subRatio) => {
          const livePercent = Math.min(95, Math.round(segBasePercent + (subRatio * segWeight)));
          onProgress({
            percent: livePercent,
            text: `Processing clip ${i + 1} of ${totalSegs} (${Math.round(subRatio * 100)}%)...`,
            currentChunk: i + 1,
            totalChunks: totalSegs
          });
        });
        clipUrl = URL.createObjectURL(clipBlob);
      } catch (err) {
        if (videoFile) {
          const sliceRatioStart = seg.start / totalDuration;
          const sliceRatioEnd = seg.end / totalDuration;
          const byteStart = Math.floor(videoFile.size * sliceRatioStart);
          const byteEnd = Math.floor(videoFile.size * sliceRatioEnd);
          clipBlob = videoFile.slice(byteStart, byteEnd, videoFile.type || 'video/mp4');
          clipUrl = URL.createObjectURL(clipBlob);
        }
      }

      if (clipBlob) {
        const padNum = String(seg.index).padStart(2, '0');
        results.push({
          id: seg.index,
          name: `${baseName}_part_${padNum}.mp4`,
          url: clipUrl,
          blob: clipBlob,
          duration: seg.duration,
          start: seg.start,
          end: seg.end,
          thumbnailUrl: thumbUrl
        });
      }

      const segEndPercent = Math.round(baseProgress + ((i + 1) / totalSegs) * allocProgress);
      onProgress({
        percent: segEndPercent,
        text: `Clip ${i + 1} of ${totalSegs} complete!`,
        currentChunk: i + 1,
        totalChunks: totalSegs
      });
    }

    if (document.body.contains(video)) {
      document.body.removeChild(video);
    }

    onProgress({ percent: 100, text: 'Splitting complete!' });
    return results;
  }

  private static captureThumbnail(video: HTMLVideoElement, timeSec: number): Promise<string> {
    return new Promise((resolve) => {
      video.currentTime = Math.max(0, timeSec);
      const handleSeeked = () => {
        video.removeEventListener('seeked', handleSeeked);
        try {
          const canvas = document.createElement('canvas');
          canvas.width = 160;
          canvas.height = 90;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
            resolve(dataUrl);
            return;
          }
        } catch (e) {
          console.warn("Canvas thumbnail failed:", e);
        }
        resolve('');
      };
      video.addEventListener('seeked', handleSeeked);
    });
  }

  private static recordVideoSegment(video: HTMLVideoElement, startSec: number, endSec: number, onSubProgress?: (ratio: number) => void): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const segDuration = endSec - startSec;
      const durationMs = segDuration * 1000;
      video.currentTime = startSec;

      let subTimer: any = null;

      const cleanup = () => {
        if (subTimer) clearInterval(subTimer);
      };

      const handleSeeked = () => {
        video.removeEventListener('seeked', handleSeeked);

        let stream: MediaStream;
        if (typeof (video as any).captureStream === 'function') {
          stream = (video as any).captureStream();
        } else if (typeof (video as any).mozCaptureStream === 'function') {
          stream = (video as any).mozCaptureStream();
        } else {
          cleanup();
          return reject(new Error("Browser does not support video.captureStream()"));
        }

        let options: MediaRecorderOptions = { mimeType: 'video/webm;codecs=vp9,opus' };
        if (!MediaRecorder.isTypeSupported(options.mimeType!)) {
          options = { mimeType: 'video/webm' };
          if (!MediaRecorder.isTypeSupported(options.mimeType!)) {
            options = { mimeType: 'video/mp4' };
          }
        }

        let mediaRecorder: MediaRecorder;
        try {
          mediaRecorder = new MediaRecorder(stream, options);
        } catch (e) {
          try {
            mediaRecorder = new MediaRecorder(stream);
          } catch (err) {
            cleanup();
            return reject(err);
          }
        }

        const chunks: Blob[] = [];
        mediaRecorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) {
            chunks.push(e.data);
          }
        };

        mediaRecorder.onstop = () => {
          cleanup();
          video.pause();
          const finalBlob = new Blob(chunks, { type: mediaRecorder.mimeType || 'video/mp4' });
          resolve(finalBlob);
        };

        mediaRecorder.onerror = (err) => {
          cleanup();
          reject(err);
        };

        mediaRecorder.start(100);
        video.play().catch((err) => {
          cleanup();
          reject(err);
        });

        subTimer = setInterval(() => {
          const elapsed = Math.max(0, video.currentTime - startSec);
          const ratio = Math.min(1.0, elapsed / Math.max(0.1, segDuration));
          if (onSubProgress) onSubProgress(ratio);
        }, 150);

        setTimeout(() => {
          if (mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
          }
        }, durationMs + 200);
      };

      video.addEventListener('seeked', handleSeeked);
    });
  }
}
